import { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Alert, Card, Button, Modal, TextInput, Tooltip, Spinner } from "flowbite-react";
import { loadFotosAction, deletarFotoAction } from "../../actions/foto";
import { HiOutlineTrash } from "react-icons/hi";
import { FaSortAmountDown, FaSortAmountUp, FaSearch } from "react-icons/fa";
import { HiOutlineShieldCheck } from "react-icons/hi2";
import FotoAuditoriaModal from "./FotoAuditoriaModal";
import { hasPlanAccess } from "../../utils/subscription";

const LIMIT = 30;

const Fotos = () => {
  const dispatch = useDispatch();
  const { fotos, page, totalPages, total, loading } = useSelector((state) => state.fotos);
  const lojaId = useSelector((state) => state.user.loja?.id);
  const profile = useSelector((state) => state.user.profile || null);
  const assinaturaAtiva = hasPlanAccess(profile);

  const [modalOpen, setModalOpen] = useState(false);
  const [fotoSelecionada, setFotoSelecionada] = useState(null);
  const [sortOrder, setSortOrder] = useState("newest");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [auditModalOpen, setAuditModalOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setCurrentPage(1);
    }, 250);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortOrder]);

  useEffect(() => {
    if (!lojaId) return;

    dispatch(
      loadFotosAction({
        lojaId,
        page: currentPage,
        limit: LIMIT,
        search: debouncedSearch,
        sortOrder,
      })
    );
  }, [dispatch, lojaId, currentPage, debouncedSearch, sortOrder]);

  const formatarNomeProduto = (url) => {
    if (!url) return "Produto";
    const partes = url.split("/");
    const nomeArquivo = partes[partes.length - 1];
    const nomeSemExtensao = nomeArquivo.replace(/_\d+\.webp$/, "");
    return nomeSemExtensao.replace(/_/g, " ");
  };

  const handleDeleteClick = (foto) => {
    if (!assinaturaAtiva) return;
    setFotoSelecionada(foto);
    setModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!fotoSelecionada || !lojaId || !assinaturaAtiva) return;

    try {
      await dispatch(deletarFotoAction(fotoSelecionada.id));
      setModalOpen(false);

      const nextPage = fotos.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      setCurrentPage(nextPage);

      dispatch(
        loadFotosAction({
          lojaId,
          page: nextPage,
          limit: LIMIT,
          search: debouncedSearch,
          sortOrder,
        })
      );
    } catch {
      // feedback já tratado via notificação global
    }
  };

  const resumoPaginacao = useMemo(() => {
    if (total === 0) return "0 resultados";

    const inicio = (page - 1) * LIMIT + 1;
    const fim = Math.min(page * LIMIT, total);
    return `${inicio}-${fim} de ${total} fotos`;
  }, [page, total]);

  return (
    <div className="container mx-auto p-4">
      <h2 className="text-xl md:text-2xl font-semibold text-gray-700 mt-8 lg:mt-2 mb-4 select-none cursor-default">
        Fotos
      </h2>

      {!assinaturaAtiva ? (
        <div className="mb-4">
          <Alert color="warning">
            Seu plano esta inativo. A exclusao e limpeza de fotos foram
            bloqueadas até reativar em <b>/app/planos</b>.
          </Alert>
        </div>
      ) : null}

      <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
        <TextInput
          placeholder="Pesquisar por nome do produto..."
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full md:w-1/2"
          icon={FaSearch}
        />
        <div className="flex gap-2">
          <Tooltip content="Auditar consistência entre banco e arquivos">
            <Button
              color="light"
              onClick={() => setAuditModalOpen(true)}
              className="flex items-center"
            >
              <HiOutlineShieldCheck className="mr-2 h-4 w-4" />
              Auditar
            </Button>
          </Tooltip>
          <Tooltip content="Mais novos">
            <Button
              onClick={() => setSortOrder("newest")}
              gradientDuoTone="purpleToPink"
              outline={sortOrder !== "newest"}
            >
              <FaSortAmountDown />
            </Button>
          </Tooltip>
          <Tooltip content="Mais antigos">
            <Button
              onClick={() => setSortOrder("oldest")}
              gradientDuoTone="purpleToPink"
              outline={sortOrder !== "oldest"}
            >
              <FaSortAmountUp />
            </Button>
          </Tooltip>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
        <span>{resumoPaginacao}</span>
        <span>Página {page} de {totalPages}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner size="xl" />
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {fotos.length > 0 ? (
            fotos.map((foto) => (
              <Card
                key={foto.id}
                imgSrc={`${process.env.REACT_APP_UPLOADS_URL}${foto.url}`}
                imgAlt={formatarNomeProduto(foto.url)}
                className="rounded-lg"
              >
                <div className="text-center text-sm text-gray-700 mt-2 space-y-1">
                  <p className="text-xs text-gray-500">{foto.produto?.referencia}</p>
                  <p className="font-semibold">{foto.produto?.descricao || "Produto"}</p>
                </div>
                <Button
                  color="warning"
                  onClick={() => handleDeleteClick(foto)}
                  disabled={!assinaturaAtiva}
                  className="mt-2 flex items-center justify-center w-full"
                >
                  <HiOutlineTrash className="w-5 h-5 mr-2" />
                  Excluir
                </Button>
              </Card>
            ))
          ) : (
            <p className="text-gray-500 col-span-full text-center">Nenhuma foto disponível.</p>
          )}
        </div>
      )}

      <div className="flex justify-center gap-2 mt-6">
        <Button
          color="light"
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={page <= 1 || loading}
        >
          Anterior
        </Button>
        <Button
          color="light"
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={page >= totalPages || loading}
        >
          Próxima
        </Button>
      </div>

      <Modal show={modalOpen} size="md" onClose={() => setModalOpen(false)}>
        <Modal.Header>Confirmar Exclusão</Modal.Header>
        <Modal.Body>
          {fotoSelecionada && (
            <div className="flex flex-col items-center">
              <img
                src={`${process.env.REACT_APP_UPLOADS_URL}${fotoSelecionada.url}`}
                alt="Thumb da foto"
                className="w-40 h-auto rounded-lg shadow-md mb-4"
              />
              <p className="text-gray-700 text-center">Tem certeza que deseja excluir esta foto?</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button color="failure" onClick={handleConfirmDelete}>
            SIM, Excluir
          </Button>
          <Button color="gray" onClick={() => setModalOpen(false)}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>

      <FotoAuditoriaModal
        isOpen={auditModalOpen}
        onClose={() => setAuditModalOpen(false)}
        lojaId={lojaId}
        canEdit={assinaturaAtiva}
      />
    </div>
  );
};

export default Fotos;
